import styled, { css } from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { ITextProps, Span, useTextCss } from '../Texts/Texts';
import { FONT_WEIGHTS } from '../../fundamentals/typography/typography';
import { IGutterAsMarginProps, Space, useGutterAsMarginCss, useGutterCss } from '../../fundamentals/spaces/space';
import { IFullWidthProps, breakpoints, useFullWidthWithGuttersCss } from '../../fundamentals/breakpoints/breakpoints';
import { IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { transparentize } from 'polished';
import { PX_TO_REM, SIZES } from '../../fundamentals/sizes/sizes';
import { BaseTd, BaseThead, BaseTbody, BaseTfoot, BaseTr, BaseTable, BaseTh } from '../../common/BaseElements';
import { EORA_NUMBERS } from '../../fundamentals/numbers/eoraNumbers';
import { rgbaHexToRgbHex } from '../../fundamentals/colours/colourUtils';
import { IThemeProviderProps } from '../../themes/EoraThemeProvider';

export const tableColours = ({ palette, base, eoraCoreColours }: IColoursCollection) => ({
  tableColours: {
    th: {
      background: eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_15,
      hover: {
        background: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.TABLE_ROW_COLOR_BACKGROUND_HOVER
      },
      selected: {
        background: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.TABLE_ROW_COLOR_BACKGROUND_SELECTED
      }
    },
    td: {
      border: eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_50,
      background: eoraCoreColours.BASE.WHITE,
      zebra: {
        background: eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_3
      }
    },
    tr: {
      hover: {
        background: rgbaHexToRgbHex(eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.TABLE_ROW_COLOR_BACKGROUND_HOVER, eoraCoreColours.BASE.WHITE)
      },
      selected: {
        background: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.TABLE_ROW_COLOR_BACKGROUND_SELECTED,
        border: eoraCoreColours.STATUS_COLORS.INFO.COLOR_INFO_500
      },
      grouped: {
        background: eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_3
      }
    },
    tfoot: {
      border: palette.neutral900
    },
    frozen: {
      boxShadow: base.black.colour,
      zebra: {
        background: palette.neutral15
      }
    },
    sort: {
      on: {
        background: palette.neutral
      },
      off: {
        background: palette.neutral300
      }
    }
  }
});

type CommonTableProps = Omit<ITextProps, 'asDisplay' | 'useMarketingFont'> & { compact?: boolean };

interface IStickyCell {
  minWidth?: Space;
  maxWidth?: Space;
  left?: Space | 0;
  right?: Space | 0;
}

const useStickyCell = css<IStickyCell>`
  ${({ minWidth, maxWidth, left, right }) => css`
    ${minWidth &&
    css`
      min-width: ${PX_TO_REM[minWidth]};
    `}
    ${maxWidth &&
    css`
      max-width: ${PX_TO_REM[maxWidth]};
    `}    
  ${left !== undefined &&
    css`
      left: ${PX_TO_REM[left]};
    `}    
  ${right !== undefined &&
    css`
      right: ${PX_TO_REM[right]};
    `}
  `}
`;

export const TD_TH_GUTTER_DEFAULT = EORA_NUMBERS.CORE.SPACING.TABLE_CELL_SPACING_PADDING;
export const TD_TH_GUTTER_COMPACT = EORA_NUMBERS.CORE.SPACING.TABLE_CELL_SPACING_PADDING_COMPACT;
export const getTableGutters = (compact?: boolean) => (compact ? TD_TH_GUTTER_COMPACT : TD_TH_GUTTER_DEFAULT);

const useCellPadding = (compact: boolean) => css`
  padding: ${PX_TO_REM[getTableGutters(compact)]};
  @media only screen and (max-width: ${breakpoints[SIZES.SMALL]}) {
    padding: ${PX_TO_REM[getTableGutters(true)]};
  }
`;

const useThBorder = ({ theme }: IThemeProviderProps) => css`
  ${PX_TO_REM[1]} solid ${theme.colours.eoraCoreColours.BORDER_COLOR.COLOR_BORDER_LIGHT}
`;
export interface ITHProps extends CommonTableProps, IStickyCell {}
export const TH = styled(BaseTh).attrs<ITHProps>(({ textAlign = 'left', fontWeightCss = FONT_WEIGHTS.SEMI_BOLD, compact, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'TH');

  return {
    fontWeightCss,
    textAlign,
    ...generateTestId()
  };
})<ITHProps>`
  ${({ compact }) => useCellPadding(compact ?? false)};
  ${useTextCss};
  ${useStickyCell};
  border-bottom: ${useThBorder};
  border-top: ${useThBorder};
  vertical-align: top;
  box-sizing: content-box;

  ${Span} {
    vertical-align: top;
  }
`;

export interface ITDProps extends CommonTableProps, IStickyCell {}

export const TD = styled(BaseTd).attrs<ITDProps>(({ compact, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'TD');

  return {
    ...generateTestId()
  };
})<ITDProps>`
  ${({ compact }) => useCellPadding(compact ?? false)};
  ${useTextCss};
  ${useStickyCell};
  vertical-align: top;
  border-bottom: ${PX_TO_REM[1]} solid ${({ theme }) => theme.colours.eoraCoreColours.BORDER_COLOR.COLOR_BORDER_ULTRALIGHT};
  box-sizing: content-box;
`;

export interface ITHeadProps extends CommonTableProps {}
export const THead = styled(BaseThead).attrs<ITHeadProps>(({ ...rest }) => {
  const { generateTestId } = useTestId(rest, 'THead');

  return {
    ...generateTestId()
  };
})<ITHeadProps>``;

export interface ITBodyProps extends CommonTableProps {}
export const TBody = styled(BaseTbody).attrs<ITBodyProps>(({ ...rest }) => {
  const { generateTestId } = useTestId(rest, 'TBody');

  return {
    ...generateTestId()
  };
})<ITBodyProps>``;

export interface ITFootProps extends CommonTableProps {}
export const TFoot = styled(BaseTfoot).attrs<ITFootProps>(({ ...rest }) => {
  const { generateTestId } = useTestId(rest, 'TFoot');

  return {
    ...generateTestId()
  };
})<ITFootProps>`
  ${TD} {
    border-top: ${PX_TO_REM[1]} solid ${({ theme }) => theme.tableColours.tfoot.border};
    border-bottom: none;
  }
`;

export interface ITRProps extends CommonTableProps {
  selected?: boolean;
  grouped?: boolean;
  sticky?: boolean;
  top?: 0 | Space;
}
const TR_SELECTED_BORDER = 2;
export const TR = styled(BaseTr).attrs<ITRProps>(({ ...rest }) => {
  const { generateTestId } = useTestId(rest, 'TR');

  return {
    ...generateTestId()
  };
})<ITRProps>`
  ${useGutterCss()};
  ${useTextCss};
  ${({ selected, theme }) =>
    selected &&
    css`
      &&&&& > ${TD} {
        background-color: ${theme.tableColours.tr.selected.background};
      }
      &&&&& > ${TD}:first-child {
        border-left: ${PX_TO_REM[TR_SELECTED_BORDER]} solid ${theme.tableColours.tr.selected.border};
        padding-left: calc(${PX_TO_REM[TD_TH_GUTTER_DEFAULT]} - ${PX_TO_REM[TR_SELECTED_BORDER]});
      }
    `}
  ${({ grouped, theme }) =>
    grouped &&
    css`
      &&&&& > ${TD} {
        background-color: ${theme.tableColours.tr.grouped.background};
      }
    `}
  &&&&:hover > ${TD} {
    background-color: ${({ theme }) => theme.tableColours.tr.hover.background};
  }
  ${({ sticky, top, theme }) =>
    sticky &&
    css`
      & > ${TH} {
        position: sticky;
        // 64px is the height of the top menu, make it a prop to set it to 0 for running modules alone
        top: ${top ?? 64}px;
        z-index: 10;
        background-color: ${theme.tableColours.td.background};
      }
    `}
`;

interface IFrozenColumnsProps {
  frozenStart?: number;
  frozenEnd?: number;
  isVerticalScrolling?: boolean;
  isHorizontalScrolling?: boolean;
}

export interface ITableProps extends IGutterAsMarginProps, IFullWidthProps, IFrozenColumnsProps {
  zebra?: boolean;
}

const useFrozen = css<IFrozenColumnsProps & Pick<ITableProps, 'zebra'>>`
  ${({ frozenStart, isHorizontalScrolling, theme, zebra }) =>
    frozenStart &&
    isHorizontalScrolling &&
    css`
      & > ${TBody} > ${TR} > ${TD}, & > ${THead} > ${TR} > ${TH}, & > ${TFoot} > ${TR} > ${TD} {
        &:nth-child(-n + ${frozenStart}) {
          position: sticky;
        }
        &:nth-child(${frozenStart}) {
          box-shadow: 0 0 ${PX_TO_REM[18]} ${transparentize(0.85, theme.tableColours.frozen.boxShadow)};
          clip-path: inset(0 -${PX_TO_REM[18]} 0 0);
        }
      }
      & > ${TBody} > ${TR} > ${TD}:nth-child(-n + ${frozenStart}),
      & > ${TFoot} > ${TR} > ${TD}:nth-child(-n + ${frozenStart}) {
        background-color: ${theme.tableColours.td.background};
        z-index: 2;
      }
      ${zebra &&
      css`
        & > ${TBody} > ${TR}:nth-child(even) > ${TD}:nth-child(-n + ${frozenStart}) {
          background-color: ${theme.tableColours.frozen.zebra.background};
        }
      `}
      & > ${THead} > ${TR} > ${TH}:nth-child(-n + ${frozenStart}) {
        background-color: ${theme.tableColours.td.background};
        z-index: 4;
      }
    `}
  ${({ frozenEnd, isHorizontalScrolling, theme, zebra }) =>
    frozenEnd &&
    isHorizontalScrolling &&
    css`
      & > ${TBody} > ${TR} > ${TD}, & > ${THead} > ${TR} > ${TH}, & > ${TFoot} > ${TR} > ${TD} {
        &:nth-last-child(-n + ${frozenEnd}) {
          position: sticky;
        }
        &:nth-last-child(${frozenEnd}) {
          box-shadow: 0 0 ${PX_TO_REM[18]} ${transparentize(0.85, theme.tableColours.frozen.boxShadow)};
          clip-path: inset(0 0 0 -${PX_TO_REM[18]});
        }
      }
      & > ${TBody} > ${TR} > ${TD}:nth-last-child(-n + ${frozenEnd}),
      & > ${TFoot} > ${TR} > ${TD}:nth-last-child(-n + ${frozenEnd}) {
        background-color: ${theme.tableColours.td.background};
        z-index: 2;
      }
      ${zebra &&
      css`
        & > ${TBody} > ${TR}:nth-child(even) > ${TD}:nth-last-child(-n + ${frozenEnd}) {
          background-color: ${theme.tableColours.frozen.zebra.background};
        }
      `}
      & > ${THead} > ${TR} > ${TH}:nth-last-child(-n + ${frozenEnd}) {
        z-index: 4;
      }
    `}
  ${({ frozenStart, frozenEnd, isVerticalScrolling }) =>
    (frozenStart || frozenEnd) &&
    isVerticalScrolling &&
    css`
      & > ${THead} > ${TR} > ${TH} {
        position: sticky;
        top: 0;
        z-index: 3;
      }
    `}
`;

export const Table = styled(BaseTable).attrs<ITableProps>(({ fullWidth = true, zebra, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'Table');

  return {
    fullWidth,
    zebra,
    ...generateTestId()
  };
})<ITableProps>`
  // table reset
  border-spacing: 0;
  ${useGutterAsMarginCss()}
  ${useFullWidthWithGuttersCss}
  ${useFrozen}
  ${({ zebra, theme }) =>
    zebra &&
    css`
      & > ${TBody} > ${TR}:nth-child(even) > ${TD} {
        background-color: ${theme.tableColours.td.zebra.background};
      }
    `}
`;
